// Geçiş Yönetimi sayfası (panel bilgisayarları, kapılar, geçiş grupları, kart yetkileri, geçiş kayıtları) kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'cards.access';

export const cardsAccessGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Geçiş Yönetimi nasıl kullanılır?'),
  intro: t(`${G}.intro`, 'Turnike ve kapı panellerini, hangi kartın hangi kapıdan geçebileceğini ve yapılan geçişleri bu sayfadan yönetirsiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  navItems: [
    t(`${G}.nav.passLogs`, 'Geçiş Kayıtları'),
    t(`${G}.nav.profiles`, 'Geçiş Grupları'),
    t(`${G}.nav.cardPermissions`, 'Kart Yetkileri'),
    t(`${G}.nav.doors`, 'Kapılar'),
  ],
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık ve özet kartları'),
    tabs: t(`${G}.regions.tabs`, 'Sekmeler'),
    body: t(`${G}.regions.body`, 'Sekme içeriği'),
  },
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Panel bilgisayarını bağlayın'),
      text: t(`${G}.s1.text`, 'Panellerin bağlı olduğu bilgisayar önce eşleştirilmelidir: Cihaz Yönetimi > Geçiş Kontrol Bilgisayarı Ekle ile 11 haneli kodu üretin, ardından o bilgisayardaki local-service\'te Seçenekler > Geçiş Yönetimi sayfasına girip envanteri (paneller ve kapılar) gönderin. Özet kartları Panel Bilgisayarı, Panel, Kapı ve Yönü Atanmamış Kapı sayısını gösterir.'),
      tips: [
        t(`${G}.s1.tip1`, '"Bu bilgisayar henüz panel bildirmedi" yazıyorsa local-service\'ten envanteri gönderin.'),
        t(`${G}.s1.tip2`, '"Geçiş yönetimi tabloları bu şirket veritabanında henüz açılmamış" uyarısı çıkarsa destek ekibine başvurun.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s2.title`, '2. Kapılar: ad ve yön verin'),
      text: t(`${G}.s2.text`, 'Kapılar sekmesinde her panelin kapıları satır satır listelenir. Kapı adı, Yön (Giriş / Çıkış) ve Lokasyon girip satırdaki kaydet simgesine basın; yalnız değişen satır kaydedilir. Yönü atanmamış kapı geçiş kararlarında kullanılamaz ve gruplara eklenemez; satırda turuncu uyarı simgesi görünür.'),
      tips: [
        t(`${G}.s2.tip1`, '"Paneli düzenle" ile panelin adını, lokasyonunu, şubesini ve kapı sayısını (1, 2 veya 4) değiştirirsiniz.'),
        t(`${G}.s2.tip2`, 'Kapı sayısını azaltmak fazla kapıları pasife alır, silmez. Panel bilgisayarındaki Geçiş Yönetimi ekranından envanter gönderilirse orada seçilen sayı geçerli olur.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Geçiş Grupları: kapıları gruplayın'),
      text: t(`${G}.s3.text`, 'Grup, bir kapı kümesine verilen addır (ör. "Üretim"). "Grup Ekle" ile grubu açın, grubu seçip dahil olacak kapıları işaretleyin ve "Kapıları Kaydet"e basın. Kaydettiğinizde işaretlenmemiş kapılar gruptan çıkarılır.'),
      tips: [
        t(`${G}.s3.tip1`, 'Kaydetmeden başka gruba geçerseniz kapı seçimi kaybolur.'),
        t(`${G}.s3.tip2`, 'Kartlara atanmış bir grup silinirse o kartlar grubun kapılarından artık geçemez.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. Kart Yetkileri: kartlara grup atayın'),
      text: t(`${G}.s4.text`, 'Kartlar burada oluşturulmaz; "Kart Tanımları" düğmesi kart sayfasına götürür. Kartı kart no, cari/personel adı veya UID ile arayın, "Gruba göre" ya da "Grubu olmayanlar" ile süzün, kartları işaretleyip "Grup Ata" veya "Grup Çıkar"a basın. Bir kartın geçebileceği kapılar, atandığı grupların birleşimidir.'),
      tips: [
        t(`${G}.s4.tip1`, 'Master kart, panel bilgisayarının tüm kapılarından geçer; gruba atanması gerekmez.'),
        t(`${G}.s4.tip2`, 'Panellere yalnız aktif ve silinmemiş kartlar gönderilir.'),
        t(`${G}.s4.tip3`, 'Grup yoksa önce Geçiş Grupları sekmesinden bir grup oluşturun.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Senkron: değişiklikler panele nasıl gider?'),
      text: t(`${G}.s5.text`, 'Panel bilgisayarı güncel yetkileri en geç 60 saniyede kendisi çeker. Beklemek istemezseniz Kapılar sekmesinde bilgisayarın yanındaki "Bu bilgisayara gönder"e basın. "Senkron" çipi son senkron zamanını gösterir; 10 dakikadan uzun süre senkron olmamışsa turuncu olur.'),
      tips: [
        t(`${G}.s5.tip1`, 'Senkron gecikse de kapılar son bilinen yetkiyle çalışmaya devam eder; geçişler engellenmez.'),
        t(`${G}.s5.tip2`, 'Bilgisayar çevrimdışıysa uyarı çıkar; değişiklik kaydedilmiştir ve bilgisayar açılınca kendisi alır.'),
        t(`${G}.s5.tip3`, '"Anlık bildirim kimliği yok" yazan bilgisayara anında gönderim yapılamaz; o bilgisayar yalnız kendi yoklamasıyla güncellenir.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. Geçiş Kayıtları'),
      text: t(`${G}.s6.text`, 'İzin verilen ve reddedilen geçişlerin hepsi kaydedilir. Kart, cari veya kapı adıyla arayın; Başlangıç / Bitiş tarihi, Panel bilgisayarı, Kapı ve Sonuç (İzin verildi / Reddedildi) ile süzün. "Gerekçe" sütunu reddin nedenini, "Tanımsız kart" sistemde kayıtlı olmayan kartı gösterir.'),
      tips: [
        t(`${G}.s6.tip1`, 'Panel bilgisayarı çevrimdışıyken verilen kararlar biriktirilir ve bağlantı gelince aktarılır; bu kayıtlar ayrı bir işaretle gösterilir.'),
        t(`${G}.s6.tip2`, '"Süzgeçleri temizle" tüm filtreleri sıfırlar. Başlıktaki (i) düğmesi bu rehberi her zaman yeniden açar.'),
      ],
    },
  ],
});
